/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useEffect, useRef, useState } from "react";
import { EMAILS } from "@/lib/constants/master";

interface TermlyEmbedProps {
  /** Termly policy data ID (from your Termly dashboard) */
  dataId: string;
  /** Optional className for the container */
  className?: string;
}

/**
 * TermlyEmbed Component
 *
 * Embeds Termly legal documents (Privacy Policy, Terms of Service, etc.)
 * using Termly's embed script.
 *
 * Usage:
 * ```tsx
 * <TermlyEmbed dataId="your-termly-policy-id" />
 * ```
 */
export function TermlyEmbed({ dataId, className }: TermlyEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!dataId) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    // Check if Termly script already exists
    const existingScript = document.querySelector(
      'script[src="https://app.termly.io/embed-policy.min.js"]',
    );

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://app.termly.io/embed-policy.min.js";
      script.async = true;
      script.onload = () => {
        setIsLoading(false);
      };
      script.onerror = () => {
        setHasError(true);
        setIsLoading(false);
      };
      document.body.appendChild(script);
    } else {
      // Script already loaded
      setIsLoading(false);
    }
  }, [dataId]);

  if (hasError) {
    return (
      <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-center">
        <p className="text-destructive">
          Unable to load policy document. Please try again later or contact
          support.
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      )}
      <div
        ref={containerRef}
        data-id={dataId}
        data-type="iframe"
        data-name="termly-embed"
        className="min-h-[500px]"
      />
    </div>
  );
}

/**
 * Placeholder component for when Termly IDs are not yet configured
 */
export function TermlyPlaceholder({ policyType }: { policyType: string }) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-6 text-center">
      <h3 className="mb-2 text-lg font-medium">{policyType} Coming Soon</h3>
      <p className="text-muted-foreground">
        This policy document is being prepared. Please check back soon or
        contact us at{" "}
        <a
          href={`mailto:${EMAILS.HELLO}`}
          className="text-primary hover:underline"
        >
          {EMAILS.HELLO}
        </a>{" "}
        for questions.
      </p>
    </div>
  );
}
